import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, MessageCircle, X, Trash2, Ban, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

/**
 * Phase 28 — Live chat full panel.
 *
 * Three variants:
 *   • variant="sidebar"  — fixed right column for the creator's dashboard.
 *   • variant="drawer"   — mobile bottom sheet (toggle via "Apri chat" btn).
 *   • variant="viewer"   — inline panel below the video for spectators.
 *
 * Same messages stream as the overlay; this view shows full history,
 * input box, and (creator-only) per-message moderation buttons.
 */
const LiveChat = ({
  messages = [],
  send,
  deleteMessage,
  currentUser,
  isCreator = false,
  onBanUser,
  variant = 'viewer',
  placeholder,
}) => {
  const { t } = useTranslation('lives');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const { toast } = useToast();

  const effectivePlaceholder = placeholder || t('chat.placeholder');

  // Auto-scroll to bottom on new message
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() || !currentUser) return;
    setSending(true);
    try {
      await send(draft);
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isCreator) return;
    await deleteMessage(id);
  };

  const handleBan = async (username) => {
    if (!isCreator || !onBanUser) return;
    if (!window.confirm(t('chat.ban_confirm', { username }))) return;
    try {
      await onBanUser(username);
      toast({ title: t('chat.ban_success', { username }) });
    } catch (e) {
      toast({ title: t('chat.ban_failed'), description: e.message || '', variant: 'destructive' });
    }
  };

  const containerClass = {
    sidebar: 'flex h-[180px] w-full flex-col rounded-xl border border-border bg-card lg:h-full lg:max-h-[70vh]',
    drawer:  'flex h-[60vh] w-full flex-col rounded-t-2xl border-t border-border bg-card',
    viewer:  'flex h-80 w-full flex-col rounded-xl border border-border bg-card',
  }[variant] || 'flex h-80 w-full flex-col rounded-xl border border-border bg-card';

  return (
    <div className={containerClass} data-testid={`live-chat-${variant}`}>
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <MessageCircle className="h-4 w-4 gold-text" />
        <span className="text-sm font-semibold">{t('chat.title')}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">{t('chat.count_short', { count: messages.length })}</span>
      </header>

      <div
        ref={listRef}
        className="flex-1 space-y-2 overflow-y-auto px-3 py-3"
        data-testid="live-chat-list"
      >
        {messages.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground lg:py-6">
            {t('chat.empty')}
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`group flex items-start gap-2 ${m.type === 'tip' ? 'rounded-md bg-amber-500/10 p-1.5' : ''}`}
              data-testid={`live-chat-msg-${m.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-xs font-semibold ${m.author?.is_creator ? 'gold-text' : 'text-foreground'}`}>
                    {m.author?.username || t('chat.guest_label')}
                    {m.author?.is_creator && ' ✦'}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(m.ts || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className={`text-sm break-words ${m.type === 'highlighted' ? 'font-semibold' : ''}`}>{m.body}</p>
              </div>
              {isCreator && (
                <div className="flex shrink-0 gap-1 opacity-0 transition group-hover:opacity-100">
                  <Button
                    onClick={() => handleDelete(m.id)}
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    title={t('chat.delete_title')}
                    data-testid={`live-chat-del-${m.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  {m.author?.id !== currentUser?.id && (
                    <Button
                      onClick={() => handleBan(m.author?.username)}
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-500"
                      title={t('chat.ban_title')}
                      data-testid={`live-chat-ban-${m.author?.id}`}
                    >
                      <Ban className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {currentUser ? (
        <form onSubmit={submit} className="flex items-center gap-2 border-t border-border p-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 400))}
            placeholder={effectivePlaceholder}
            disabled={sending}
            maxLength={400}
            className="flex-1"
            data-testid="live-chat-input"
          />
          <Button
            type="submit"
            disabled={sending || !draft.trim()}
            size="icon"
            className="gold-bg shrink-0"
            data-testid="live-chat-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      ) : (
        <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
          {t('chat.login_prompt')}
        </p>
      )}
    </div>
  );
};

export default LiveChat;

// helper for parent to compose a mobile chat-toggle button
export const LiveChatToggle = ({ onClick, badge = 0 }) => {
  const { t } = useTranslation('lives');
  return (
    <Button
      onClick={onClick}
      variant="outline"
      size="sm"
      className="gap-1"
      data-testid="live-chat-toggle-btn"
    >
      <MessageCircle className="h-4 w-4" />
      {t('chat.toggle_btn')}
      {badge > 0 && (
        <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-black">{badge}</span>
      )}
    </Button>
  );
};

export { X as DrawerCloseIcon };
